import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { DeviceCommandsDocument } from '../../queries/commands.generated';
import { DeviceCommandActions } from '../lib/styles';
import { Table } from './DeviceCommands/components';

interface DeviceCommandListProps {
  deviceId: number;
  fullHeight?: boolean;
}

export const DeviceCommandList = ({ deviceId, fullHeight = false }: DeviceCommandListProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(DeviceCommandsDocument, {
    variables: {
      deviceId,
      page,
      perPage,
      search,
    },
    onCompleted: (data) => handleOutOfBounds(data.device?.deviceCommands.pageInfo),
  });

  const totalCount = useTotalCount(data?.device?.deviceCommands.pageInfo.nodesCount);

  return (
    <Box sx={fullHeight ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
      <DeviceCommandActions style={fullHeight ? { flexShrink: 0 } : undefined}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SearchBar
            onChange={(value) =>
              setSearchParams((params) => {
                params.set('search', value);
                params.set('page', '1');
                return params;
              })
            }
            placeholder={t('pages.devices.show.command_list.search_placeholder')}
            search={search}
          />
        </Box>
        <Count
          selectedCount={0}
          totalCount={totalCount || 0}
          thing="common.pluralize.command_plural"
        />
      </DeviceCommandActions>
      <Box sx={fullHeight ? { flex: 1, minHeight: 0 } : undefined}>
        <Table
          deviceCommands={data?.device?.deviceCommands.nodes ?? []}
          fullHeight={fullHeight}
          loading={loading}
          totalCount={totalCount}
        />
      </Box>
    </Box>
  );
};
